/**
 * EditorSerializer: produce a saveable `SceneManifest` from the live object list.
 *
 * The output carries both the unified `objects[]` (the source of truth the
 * runtime + editor read back) AND derived legacy `hotspots[]` / `triggerZones[]`
 * / `portals[]` arrays, so older code paths and any external tooling that still
 * reads the legacy shape keep working. Round-tripping (save → reload) restores
 * every object, event, and portal unchanged.
 *
 * Before writing, `serializeScene` validates the invariants the runtime relies
 * on and returns a list of errors/warnings; the editor decides whether to block
 * the save or proceed.
 */

import type {
    SceneManifest,
    HotspotConfig,
    TriggerZoneConfig,
    PortalConfig as LegacyPortalConfig
} from '../config/manifest';
import type { SceneObject, SceneEventAction } from '../scene/SceneObjectTypes';

export interface SerializeResult {
    manifest: SceneManifest;
    errors: string[];
    warnings: string[];
}

/** Serialize the current scene + objects into a saveable manifest. */
export function serializeScene(manifest: SceneManifest, objects: SceneObject[]): SerializeResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // ── Validation ──
    const ids = new Set<string>();
    for (const o of objects) {
        if (!o.id) { errors.push(`对象「${o.name}」缺少 id`); continue; }
        if (ids.has(o.id)) errors.push(`重复的对象 id：${o.id}`);
        ids.add(o.id);

        if (o.type === 'hotspot' && !o.interaction) {
            errors.push(`热点「${o.name}」缺少 interaction`);
        }
        if (o.type === 'portal') {
            if (!o.portal) errors.push(`传送门「${o.name}」缺少 portal 配置`);
            else if (!o.portal.targetScene) errors.push(`传送门「${o.name}」缺少 targetScene`);
        }
        if (o.interaction) {
            o.interaction.events.forEach((ev, i) => {
                if (!ev.type) errors.push(`「${o.name}」事件 #${i + 1} 缺少 type`);
                if (ev.type === 'switchScene' && !ev.payload.targetScene) {
                    errors.push(`「${o.name}」事件「${ev.name}」(switchScene) 缺少 targetScene`);
                }
            });
        }
    }

    // ── Derive legacy arrays (for back-compat with old readers) ──
    const hotspots: HotspotConfig[] = [];
    const triggerZones: TriggerZoneConfig[] = [];
    const portals: LegacyPortalConfig[] = [];

    for (const o of objects) {
        if (o.type === 'hotspot') {
            const panel = firstPanel(o);
            hotspots.push({
                id: o.id,
                name: o.name,
                position: [...o.transform.position] as [number, number, number],
                radius: o.interaction?.radius ?? 3,
                type: 'info',
                panel: panel ?? { title: o.name, content: '' },
                color: o.marker.kind === 'primitive' ? o.marker.color : undefined
            });
        } else if (o.type === 'triggerZone') {
            const panel = firstPanel(o);
            const size = o.trigger?.size ?? (o.marker.kind === 'primitive' ? o.marker.scale ?? [6, 6, 6] : [6, 6, 6]);
            triggerZones.push({
                id: o.id,
                name: o.name,
                type: 'box',
                center: [...o.transform.position] as [number, number, number],
                size: [...size] as [number, number, number],
                panel: panel
            });
        } else if (o.type === 'portal' && o.portal) {
            const size = o.marker.kind === 'primitive' ? o.marker.scale ?? [5, 5, 5] : [5, 5, 5];
            portals.push({
                id: o.id,
                name: o.name,
                position: [...o.transform.position] as [number, number, number],
                size: [...size] as [number, number, number],
                targetRoomId: o.portal.targetRoomId, targetScene: o.portal.targetScene,
                targetSpawn: [...o.portal.targetSpawn] as [number, number, number],
                targetAngles: [o.portal.targetAngles[0], o.portal.targetAngles[1]],
                color: o.portal.color ?? (o.marker.kind === 'primitive' ? o.marker.color : undefined)
            });
        }
    }

    const out: SceneManifest = {
        ...manifest,
        objects: cloneObjects(objects),
        hotspots,
        triggerZones,
        portals
    };

    return { manifest: out, errors, warnings };
}

/** The first showPanel action's payload, if any (drives the legacy `panel`). */
function firstPanel(o: SceneObject): { title: string; content: string } | undefined {
    const ev = o.interaction?.events.find((e) => e.type === 'showPanel') as SceneEventAction | undefined;
    if (!ev) return undefined;
    return { title: ev.payload.title ?? ev.name, content: ev.payload.content ?? '' };
}

/** Deep-clone the object list so the saved manifest can't be mutated by later edits. */
function cloneObjects(objects: SceneObject[]): SceneObject[] {
    return JSON.parse(JSON.stringify(objects)) as SceneObject[];
}
